import { ECardinality, ESymbolCategory } from "@kuika/kuika-cl-model";
import { CascaderOptionType } from "antd/es/cascader";
import { AttachableActionParameter } from "./attachable-actions";
import { CardinalityConversion, encodeSymbolRef, SymbolRefParsed } from "./symbol-ref";

/**
 * One entry in the bindables manifest returned alongside `ui_render`. Refs are
 * the only thing the LLM needs to hand back in a `ui_bind` call; the other
 * fields are hints so the LLM can reason about type/cardinality fit before
 * committing, without a follow-up discovery round.
 */
export interface ManifestBinding {
  ref: string;
  label: string;
  category: string;
  type?: string;
  cardinality?: ECardinality;
  /** Populated only for `actionDef:` refs — the parameters the LLM must supply when attaching. */
  parameters?: AttachableActionParameter[];
}

type AncestorChain = readonly CascaderOptionType[];

const CATEGORY_NAMES: Partial<Record<ESymbolCategory, string>> = {
  [ESymbolCategory.default]: "default",
  [ESymbolCategory.screenInput]: "screenInput",
  [ESymbolCategory.displayItemProp]: "displayItemProp",
  [ESymbolCategory.actionResult]: "actionResult",
  [ESymbolCategory.actionField]: "actionField",
  [ESymbolCategory.fieldOfType]: "fieldOfType",
  [ESymbolCategory.current]: "current",
  [ESymbolCategory.bag]: "bag"
};

const categoryName = (category: ESymbolCategory | undefined): string =>
  category !== undefined ? (CATEGORY_NAMES[category] ?? String(category)) : "unknown";

const asString = (value: unknown, fallback: string): string =>
  typeof value === "string" && value.length > 0 ? value : fallback;

const readType = (node: CascaderOptionType): string | undefined => {
  const domainType = node.domainType as { name?: string } | string | undefined;
  if (typeof domainType === "string" && domainType.length > 0) return domainType;
  if (
    domainType &&
    typeof domainType === "object" &&
    typeof domainType.name === "string" &&
    domainType.name.length > 0
  ) {
    return domainType.name;
  }
  if (typeof node.currentType === "string" && node.currentType.length > 0) return node.currentType;
  return undefined;
};

const readCardinality = (node: CascaderOptionType): ECardinality | undefined => {
  if (typeof node.cardinality === "number") return node.cardinality as ECardinality;
  return undefined;
};

const isLoadingPlaceholder = (node: CascaderOptionType): boolean =>
  node.dynamicTypesOption === true || node.loading === true;

const hasChildren = (node: CascaderOptionType): boolean => Array.isArray(node.children) && node.children.length > 0;

const findAncestor = (ancestors: AncestorChain, category: ESymbolCategory): CascaderOptionType | undefined => {
  for (let i = ancestors.length - 1; i >= 0; i--) {
    if (ancestors[i].category === category) return ancestors[i];
  }
  return undefined;
};

const isValidConversion = (value: string): value is CardinalityConversion =>
  value === "first" || value === "last" || value === "selected";

const readConverterName = (node: CascaderOptionType): string | undefined => {
  const direct = asString(node.converterName, "");
  if (direct !== "") return direct;
  const label = asString(node.label, "");
  const match = label.match(/\.?(first|last|selected)\(\)/);
  return match?.[1];
};

const hasCardinalityConverterChild = (node: CascaderOptionType): boolean =>
  Array.isArray(node.children) &&
  node.children.some((child) => child.category === ESymbolCategory.cardinalityConvertion);

const buildDiRef = (diId: number, propName: string, conversion: string | undefined): string => {
  const parsed: SymbolRefParsed =
    conversion && isValidConversion(conversion)
      ? { kind: "di", kuikaId: String(diId), propName, conversion }
      : { kind: "di", kuikaId: String(diId), propName };
  return encodeSymbolRef(parsed);
};

/**
 * Build the `fieldPath` for a fieldOfType leaf by joining every fieldOfType
 * ancestor's label from the nearest enclosing action or current node downward.
 * Intermediate fieldOfType nodes that have further fieldOfType children are
 * NOT leaves themselves; we only emit at terminal positions.
 */
const collectFieldPath = (node: CascaderOptionType, ancestors: AncestorChain, boundary: ESymbolCategory): string => {
  const parts: string[] = [];
  let insideBoundary = false;
  for (const ancestor of ancestors) {
    if (ancestor.category === boundary) {
      insideBoundary = true;
      continue;
    }
    if (!insideBoundary) continue;
    if (ancestor.category === ESymbolCategory.fieldOfType) {
      parts.push(asString(ancestor.fieldName, asString(ancestor.label, "")));
    }
  }
  parts.push(asString(node.fieldName, asString(node.label, "")));
  return parts.filter((p) => p.length > 0).join(".");
};

const isFieldOfTypeLeaf = (node: CascaderOptionType): boolean => {
  if (node.category !== ESymbolCategory.fieldOfType) return false;
  if (!hasChildren(node)) return true;
  return node.children!.every((child: CascaderOptionType) => child.category !== ESymbolCategory.fieldOfType);
};

const emitForNode = (node: CascaderOptionType, ancestors: AncestorChain, out: ManifestBinding[]): void => {
  if (isLoadingPlaceholder(node)) return;

  const type = readType(node);
  const cardinality = readCardinality(node);
  const label = asString(node.label, "");

  switch (node.category) {
    case ESymbolCategory.default: {
      if (hasChildren(node)) return;
      const name = label;
      if (name === "") return;
      out.push({
        ref: encodeSymbolRef({ kind: "default", name }),
        label: name,
        category: categoryName(ESymbolCategory.default),
        type,
        cardinality: cardinality ?? ECardinality.One
      });
      return;
    }
    case ESymbolCategory.screenInput: {
      const inputName = asString(node.inputName, "");
      if (inputName === "") return;
      out.push({
        ref: encodeSymbolRef({ kind: "screenInput", name: inputName }),
        label: asString(label, inputName),
        category: categoryName(ESymbolCategory.screenInput),
        type,
        cardinality
      });
      return;
    }
    case ESymbolCategory.displayItemProp: {
      if (hasCardinalityConverterChild(node)) return;
      const diAncestor = findAncestor(ancestors, ESymbolCategory.displayItem);
      const diId = diAncestor?.diId;
      if (typeof diId !== "number") return;
      const propName = label;
      if (propName === "") return;
      out.push({
        ref: buildDiRef(diId, propName, undefined),
        label: asString(diAncestor?.label, `di:${diId}`) + "." + propName,
        category: categoryName(ESymbolCategory.displayItemProp),
        type,
        cardinality
      });
      return;
    }
    case ESymbolCategory.cardinalityConvertion: {
      const converter = readConverterName(node);
      if (!converter || !isValidConversion(converter)) return;
      const propAncestor = findAncestor(ancestors, ESymbolCategory.displayItemProp);
      if (!propAncestor) return;
      const diAncestor = findAncestor(ancestors, ESymbolCategory.displayItem);
      const diId = diAncestor?.diId;
      if (typeof diId !== "number") return;
      const propName = asString(propAncestor.label, "");
      if (propName === "") return;
      out.push({
        ref: buildDiRef(diId, propName, converter),
        label: `${asString(diAncestor?.label, `di:${diId}`)}.${propName}.${converter}()`,
        category: categoryName(ESymbolCategory.displayItemProp),
        type: type ?? readType(propAncestor),
        cardinality: cardinality ?? ECardinality.One
      });
      return;
    }
    case ESymbolCategory.actionResult: {
      const resultActionId = asString(node.resultActionId, "");
      if (resultActionId === "") return;
      out.push({
        ref: encodeSymbolRef({ kind: "actionResult", resultActionId }),
        label: asString(label, resultActionId),
        category: categoryName(ESymbolCategory.actionResult),
        type,
        cardinality
      });
      return;
    }
    case ESymbolCategory.fieldOfType: {
      if (!isFieldOfTypeLeaf(node)) return;
      const actionAncestor = findAncestor(ancestors, ESymbolCategory.actionResult);
      if (actionAncestor) {
        const resultActionId = asString(actionAncestor.resultActionId, "");
        if (resultActionId === "") return;
        const fieldPath = collectFieldPath(node, ancestors, ESymbolCategory.actionResult);
        if (fieldPath === "") return;
        out.push({
          ref: encodeSymbolRef({ kind: "actionField", resultActionId, fieldPath }),
          label: fieldPath,
          category: categoryName(ESymbolCategory.actionField),
          type,
          cardinality
        });
        return;
      }
      const currentAncestor = findAncestor(ancestors, ESymbolCategory.current);
      if (currentAncestor) {
        const fieldPath = collectFieldPath(node, ancestors, ESymbolCategory.current);
        if (fieldPath === "") return;
        out.push({
          ref: encodeSymbolRef({ kind: "current", fieldPath }),
          label: fieldPath,
          category: categoryName(ESymbolCategory.current),
          type,
          cardinality
        });
        return;
      }
      return;
    }
    case ESymbolCategory.bag: {
      const bagName = asString(node.bagName, label);
      if (bagName === "") return;
      out.push({
        ref: encodeSymbolRef({ kind: "bag", bagName }),
        label: bagName,
        category: categoryName(ESymbolCategory.bag),
        type,
        cardinality
      });
      return;
    }
    default:
      return;
  }
};

const walk = (nodes: CascaderOptionType[], ancestors: AncestorChain, out: ManifestBinding[]): void => {
  for (const node of nodes) {
    emitForNode(node, ancestors, out);
    if (hasChildren(node)) {
      walk(node.children!, [...ancestors, node], out);
    }
  }
};

/**
 * Walk a cascader tree and produce a flat list of bindable leaves encoded as
 * symbolRefs. Loading placeholders are skipped. This is the pure half of the
 * bindables manifest builder; the stateful half (which creates the engine,
 * iterates properties, aggregates per component) calls into this.
 */
export const extractLeafOptions = (options: CascaderOptionType[]): ManifestBinding[] => {
  const out: ManifestBinding[] = [];
  walk(options, [], out);
  return out;
};
